import { ChevronDown, LanguagesIcon } from 'lucide-react';
import React from 'react';

import {
  Box,
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
} from '@eluve/components';
import {
  SupportedLanguages,
  SupportedLanguagesByCode,
} from '@eluve/language-utils';

type LanguageSelectorComboBoxProps = {
  selectedLanguageCode: string;
  onSelectedLanguageCode: (languageCode: string) => Promise<void>;
  disabled?: boolean;
  preferredLanguages?: string[];
  selectButtonStyles?: string;
  showLanguageIcon?: boolean;
  ariaLabelPrefix?: string;
};

export const LanguageSelectorComboBox: React.FC<
  LanguageSelectorComboBoxProps
> = ({
  selectedLanguageCode,
  onSelectedLanguageCode,
  disabled = false,
  preferredLanguages = [],
  selectButtonStyles = '',
  showLanguageIcon = true,
  ariaLabelPrefix = 'Language',
}) => {
  const sortedLanguages = Object.entries(SupportedLanguages).sort(
    ([, codeA], [, codeB]) => {
      const isCodeAPreferred = preferredLanguages.includes(codeA) ? 0 : 1;
      const isCodeBPreferred = preferredLanguages.includes(codeB) ? 0 : 1;
      return isCodeAPreferred - isCodeBPreferred;
    },
  );

  return (
    <Combobox disabled={disabled}>
      <ComboboxSelectButton
        className={selectButtonStyles}
        aria-label={`${ariaLabelPrefix} Selector`}
        dropdownIcon={
          <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
        }
      >
        <Box hStack className="w-full overflow-hidden">
          {showLanguageIcon && <LanguagesIcon size={16} />}
          {SupportedLanguagesByCode[selectedLanguageCode]}
        </Box>
      </ComboboxSelectButton>
      <ComboboxDropdown
        searchPlaceholder="Select Language"
        className="min-w-52"
        aria-label={`${ariaLabelPrefix} Dropdown`}
      >
        {sortedLanguages.map(([languageName, availableLanguageCode]) => (
          <ComboboxOption
            key={availableLanguageCode}
            onSelect={async () => {
              await onSelectedLanguageCode(availableLanguageCode);
            }}
          >
            <ComboboxSelectCheck
              className="mr-3"
              selected={availableLanguageCode === selectedLanguageCode}
            />
            {languageName}
          </ComboboxOption>
        ))}
      </ComboboxDropdown>
    </Combobox>
  );
};
